import {
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useEffect } from "react";
import { useApi } from "@/core/api";
import { useAuth } from "@/core/auth";
import { isTemporaryPackChatError } from "./thread-error-copy";

interface Validator {
  etag: string;
  endpoint: string;
}
interface ResponseCache {
  scope: string;
  bodies: WeakMap<object, Validator>;
}
const caches = new WeakMap<QueryClient, ResponseCache>();

/** Validators belong to the exact immutable response, never a mutation-updated copy. */
export const conditionalPackChatRequest = async <T extends object>(
  api: AxiosInstance,
  client: QueryClient,
  scope: string,
  endpoint: string,
  key: QueryKey,
  signal: AbortSignal,
): Promise<T> => {
  let cache = caches.get(client);
  if (!cache || cache.scope !== scope) {
    cache = { scope, bodies: new WeakMap() };
    caches.set(client, cache);
  }
  const body = client.getQueryData<T>(key);
  const validator = body && cache.bodies.get(body);
  const etag = validator?.endpoint === endpoint ? validator.etag : undefined;
  try {
    const response = await api.get<T>(endpoint, {
      signal,
      headers: etag ? { "If-None-Match": etag } : undefined,
      validateStatus: (status) =>
        status === 304 || (status >= 200 && status < 300),
    });
    // Axios adapters and token-refresh replays must not publish canceled responses.
    signal.throwIfAborted();
    if (response.status === 304) {
      if (!body || !etag)
        throw new Error("PackChat returned 304 without a cached response");
      return body;
    }
    const next = response.data;
    const nextEtag = response.headers?.etag;
    if (typeof nextEtag === "string" && next && typeof next === "object") {
      cache.bodies.set(next, { etag: nextEtag, endpoint });
    }
    return next;
  } catch (error) {
    if (!signal.aborted && !isTemporaryPackChatError(error)) {
      if (body) cache.bodies.delete(body);
      // Keep the query error but remove the old response, including from inactive observers.
      const query = client.getQueryCache().find({ queryKey: key, exact: true });
      query?.setState({ data: undefined });
    }
    throw error;
  }
};

export const usePackChatRequest = (businessId: string | null) => {
  const api = useApi();
  const client = useQueryClient();
  const { user } = useAuth();
  const scope = JSON.stringify([user?.id, businessId]);
  useEffect(() => {
    // Sign-out and impersonation already clear QueryClient in the auth providers.
    // A business switch must also discard inactive PackChat bodies.
    client.removeQueries({
      predicate: (query) =>
        ["pack-threads", "managed-pack-threads"].includes(
          String(query.queryKey[0]),
        ) && query.queryKey[1] !== businessId,
    });
  }, [client, user?.id, businessId]);
  return <T extends object>(
    endpoint: string,
    key: QueryKey,
    signal: AbortSignal,
  ) => conditionalPackChatRequest<T>(api, client, scope, endpoint, key, signal);
};
